<template>
  <div class="scoreboard-container">
    <!-- Scanline / texture layer -->
    <div class="texture-layer"></div>

    <v-container class="relative" fluid>
      <v-row>
        <v-col cols="12">

          <!-- ===== BREADCRUMB ===== -->
          <div class="crumb-rail">
            <div class="crumb-item" @click="$router.push({ name: 'competition' })">
              <v-icon color="#4FC3F7" size="16">mdi-trophy</v-icon>
              <span>Challenges</span>
            </div>
            <span class="crumb-divider">/</span>
            <div class="crumb-item active">
              <span>Dashboard</span>
            </div>
          </div>

          <!-- ===== HERO SCOREBOARD ===== -->
          <div class="hero-board">
            <div class="hero-board-inner">
              <div class="hero-left">
                <span class="eyebrow">// TEAM CHALLENGE CONSOLE</span>
                <h1 class="hero-title">Challenge<br />Dashboard</h1>
                <p class="hero-sub">Every match you've run, joined, or been invited to &mdash; tracked in one place.</p>
              </div>

              <div class="hero-right">
                <div class="flip-tile" v-if="participatedCount > 0">
                  <span class="flip-number">{{ pad(participatedCount) }}</span>
                  <span class="flip-label">Participated</span>
                </div>
                <div class="flip-tile accent-lime" v-if="myCreatedCount > 0 && profile.type === 'Agency'">
                  <span class="flip-number">{{ pad(myCreatedCount) }}</span>
                  <span class="flip-label">My Created</span>
                </div>
                <div class="flip-tile accent-coral" v-if="invitationsCount > 0 && profile.type === 'Agency'">
                  <span class="flip-number">{{ pad(invitationsCount) }}</span>
                  <span class="flip-label">Invitations</span>
                </div>
                <div class="flip-tile accent-blue" v-if="requestsCount > 0">
                  <span class="flip-number">{{ pad(requestsCount) }}</span>
                  <span class="flip-label">Requests</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ===== MAIN PANEL ===== -->
          <v-card class="main-panel" flat>

            <!-- Segment control tabs -->
            <div class="segment-control">
              <button
                class="segment-btn"
                :class="{ active: activeTab === 'participated' }"
                @click="activeTab = 'participated'"
              >
                <span class="seg-label">Participated</span>
                <span class="seg-count" v-if="participatedCount">{{ participatedCount }}</span>
              </button>

              <button
                v-if="profile.type === 'Agency'"
                class="segment-btn"
                :class="{ active: activeTab === 'my-created' }"
                @click="activeTab = 'my-created'"
              >
                <span class="seg-label">My Created</span>
                <span class="seg-count lime" v-if="myCreatedCount">{{ myCreatedCount }}</span>
              </button>

              <button
                v-if="profile.type === 'Agency'"
                class="segment-btn"
                :class="{ active: activeTab === 'invitations' }"
                @click="activeTab = 'invitations'"
              >
                <span class="seg-label">Invitations</span>
                <span class="seg-count coral" v-if="invitationsCount">{{ invitationsCount }}</span>
              </button>

              <button
                class="segment-btn"
                :class="{ active: activeTab === 'requests' }"
                @click="activeTab = 'requests'"
              >
                <span class="seg-label">Requests</span>
                <span class="seg-count blue" v-if="requestsCount">{{ requestsCount }}</span>
              </button>
            </div>

            <div class="panel-body">

              <!-- ==================== PARTICIPATED TAB ==================== -->
              <div v-show="activeTab === 'participated'" class="tab-panel">
                <div v-if="loading" class="skeleton-grid">
                  <div v-for="i in 3" :key="i" class="skeleton-ticket">
                    <div class="skeleton-bar"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                </div>

                <div v-else-if="participatedCount === 0" class="empty-state">
                  <div class="empty-mark">00</div>
                  <h3 class="empty-title">No matches on record</h3>
                  <p class="empty-description">You haven't joined a team challenge yet. First whistle is on you.</p>
                  <button class="btn-primary" @click="explore">Explore Challenges →</button>
                </div>

                <div v-else class="content-section">
                  <div class="control-row">
                    <div class="control-group">
                      <div class="search-wrapper">
                        <v-icon size="16" color="#80DEEA">mdi-magnify</v-icon>
                        <input v-model="search" type="text" class="search-input" placeholder="Search challenges…" />
                      </div>
                    </div>
                    <div class="control-group">
                      <select v-model="sortBy" class="sort-select">
                        <option v-for="opt in sortOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                      </select>
                      <div class="view-toggle">
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'grid' }"
                          @click="viewMode = 'grid'"
                          title="Grid View"
                        >
                          <v-icon size="20">mdi-view-grid</v-icon>
                        </button>
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'table' }"
                          @click="viewMode = 'table'"
                          title="Table View"
                        >
                          <v-icon size="20">mdi-view-list</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="filter-rail">
                    <button
                      v-for="filter in filters"
                      :key="filter.value"
                      class="filter-tag"
                      :class="{ active: activeFilter === filter.value }"
                      @click="activeFilter = filter.value"
                    >
                      {{ filter.label }} <span class="tag-count">{{ getFilterCount(filter.value) }}</span>
                    </button>
                  </div>

                  <!-- Grid View -->
                  <div v-if="viewMode === 'grid'" class="ticket-grid">
                    <div v-for="(item, index) in filteredParticipated" :key="index" class="ticket-card">
                      <div class="ticket-bar" :class="getStatusClass(item.response_status)"></div>
                      <div class="ticket-notch left"></div>
                      <div class="ticket-notch right"></div>

                      <div class="ticket-head">
                        <h3 class="ticket-title">{{ truncateText(item.competitionId.title, 28) }}</h3>
                        <div class="status-tag" :class="getStatusClass(item.competitionId?.competition_status)">
                          {{ item.competitionId?.competition_status || 'N/A' }}
                        </div>
                      </div>

                      <div class="ticket-perf"></div>

                      <div class="ticket-body">
                        <div class="ticket-row">
                          <span class="ticket-label">Organizer</span>
                          <div class="organizer-info">
                            <img :src="item.createdBy?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <span>{{ truncateName(item.createdBy.name) }}</span>
                          </div>
                        </div>
                        <div class="ticket-row">
                          <span class="ticket-label">Join Type</span>
                          <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                        </div>
                        <div class="ticket-row split">
                          <div>
                            <span class="ticket-label">Points</span>
                            <span class="mono-stat">{{ item.competitionId.target_points }}</span>
                          </div>
                          <div>
                            <span class="ticket-label">Requested</span>
                            <span class="mono-date">{{ formatDate(item.response_date) }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="ticket-footer">
                        <button class="action-btn primary" @click="viewChallenge(item)">View</button>
                        <button
                          v-if="profile.type === 'Agency' && item.join_type !== 'Creator' && item.response_status === 'Accepted'"
                          class="action-btn success"
                          @click="respondToChallenge(item)"
                        >
                          Respond
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Table View -->
                  <div v-else class="table-wrapper">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Challenge</th>
                          <th>Organizer</th>
                          <th>Join Type</th>
                          <th>Points</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in filteredParticipated" :key="index">
                          <td class="table-title">
                            <span class="status-dot" :class="getStatusClass(item.response_status)"></span>
                            {{ truncateText(item.competitionId.title, 35) }}
                          </td>
                          <td>
                            <div class="organizer-info">
                              <img :src="item.createdBy?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <span>{{ truncateName(item.createdBy.name, 20) }}</span>
                            </div>
                          </td>
                          <td>
                            <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                          </td>
                          <td class="mono-stat">{{ item.competitionId.target_points }}</td>
                         <td>
                          <span class="status-tag" :class="getStatusClass(item.competitionId?.competition_status)">
                            {{ item.competitionId?.competition_status || 'N/A' }}
                          </span>
                        </td>
                          <td class="mono-date">{{ formatDate(item.response_date) }}</td>
                          <td>
                            <div class="table-actions">
                              <button class="action-btn primary small" @click="viewChallenge(item)">View</button>
                              <button
                                v-if="profile.type === 'Agency' && item.join_type !== 'Creator' && item.response_status === 'Accepted'"
                                class="action-btn success small"
                                @click="respondToChallenge(item)"
                              >
                                Respond
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ==================== MY CREATED TAB ==================== -->
              <div v-show="activeTab === 'my-created'" v-if="profile.type === 'Agency'" class="tab-panel">
                <div v-if="loading" class="skeleton-grid">
                  <div v-for="i in 3" :key="i" class="skeleton-ticket">
                    <div class="skeleton-bar"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                </div>

                <div v-else-if="myCreatedCount === 0" class="empty-state">
                  <div class="empty-mark">00</div>
                  <h3 class="empty-title">Nothing created yet</h3>
                  <p class="empty-description">Launch a team challenge and watch requests roll in.</p>
                  <button class="btn-primary" @click="createChallenge">+ Create Challenge</button>
                </div>

                <div v-else class="content-section">
                  <div class="control-row single">
                    <div class="control-group">
                      <div class="search-wrapper">
                        <v-icon size="16" color="#80DEEA">mdi-magnify</v-icon>
                        <input v-model="searchMyCreated" type="text" class="search-input" placeholder="Search my challenges…" />
                      </div>
                    </div>
                    <div class="control-group">
                      <div class="view-toggle">
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'grid' }"
                          @click="viewMode = 'grid'"
                          title="Grid View"
                        >
                          <v-icon size="20">mdi-view-grid</v-icon>
                        </button>
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'table' }"
                          @click="viewMode = 'table'"
                          title="Table View"
                        >
                          <v-icon size="20">mdi-view-list</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Grid View -->
                  <div v-if="viewMode === 'grid'" class="ticket-grid">
                    <div v-for="(item, index) in filteredMyCreated" :key="index" class="ticket-card">
                      <div class="ticket-bar" :class="getStatusClass(item.competition_status)"></div>
                      <div class="ticket-notch left"></div>
                      <div class="ticket-notch right"></div>

                      <div class="ticket-head">
                        <h3 class="ticket-title">{{ truncateText(item.title, 28) }}</h3>
                        <div class="status-tag" :class="getStatusClass(item.competition_status)">{{ item.competition_status }}</div>
                      </div>

                      <div class="ticket-perf"></div>

                      <div class="ticket-body">
                        <div class="ticket-row split">
                          <div>
                            <span class="ticket-label">Points</span>
                            <span class="mono-stat">{{ item.target_points }}</span>
                          </div>
                          <div>
                            <span class="ticket-label">Responses</span>
                            <span class="response-count">{{ item.responsesCount || 0 }}</span>
                          </div>
                        </div>
                        <div class="ticket-row split">
                          <div>
                            <span class="ticket-label">Start</span>
                            <span class="mono-date">{{ formatDate(item.start_date) }}</span>
                          </div>
                          <div>
                            <span class="ticket-label">End</span>
                            <span class="mono-date">{{ formatDate(item.end_date) }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="ticket-footer">
                        <button class="action-btn primary" @click="viewChallengeDetails(item)">View</button>
                        <button v-if="item.competition_status !== 'Completed'" class="action-btn warning" @click="viewResponses(item)">
                          Responses
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Table View -->
                  <div v-else class="table-wrapper">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Challenge</th>
                          <th>Points</th>
                          <th>Responses</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in filteredMyCreated" :key="index">
                          <td class="table-title">
                            <span class="status-dot" :class="getStatusClass(item.competition_status)"></span>
                            {{ truncateText(item.title, 35) }}
                          </td>
                          <td class="mono-stat">{{ item.target_points }}</td>
                          <td><span class="response-count">{{ item.responsesCount || 0 }}</span></td>
                          <td>
                            <span class="status-tag" :class="getStatusClass(item.competition_status)">{{ item.competition_status }}</span>
                          </td>
                          <td class="mono-date">{{ formatDate(item.start_date) }}</td>
                          <td class="mono-date">{{ formatDate(item.end_date) }}</td>
                          <td>
                            <div class="table-actions">
                              <button class="action-btn primary small" @click="viewChallengeDetails(item)">View</button>
                              <button v-if="item.competition_status !== 'Completed'" class="action-btn warning small" @click="viewResponses(item)">
                                Responses
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ==================== INVITATIONS TAB ==================== -->
              <div v-show="activeTab === 'invitations'" v-if="profile.type === 'Agency'" class="tab-panel">
                <div v-if="loading" class="skeleton-grid">
                  <div v-for="i in 3" :key="i" class="skeleton-ticket">
                    <div class="skeleton-bar"></div>
                    <div class="skeleton-line"></div>
                  </div>
                </div>

                <div v-else-if="invitationsCount === 0" class="empty-state">
                  <div class="empty-mark">00</div>
                  <h3 class="empty-title">No invitations pending</h3>
                  <p class="empty-description">When an agency invites your team, it'll land right here.</p>
                </div>

                <div v-else class="content-section">
                  <div class="control-row single">
                    <div class="control-group">
                      <div class="view-toggle">
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'grid' }"
                          @click="viewMode = 'grid'"
                          title="Grid View"
                        >
                          <v-icon size="20">mdi-view-grid</v-icon>
                        </button>
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'table' }"
                          @click="viewMode = 'table'"
                          title="Table View"
                        >
                          <v-icon size="20">mdi-view-list</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Grid View -->
                  <div v-if="viewMode === 'grid'" class="ticket-grid">
                    <div v-for="(item, index) in invitationsData" :key="index" class="ticket-card">
                      <div class="ticket-bar" :class="getStatusClass(item.response_status)"></div>
                      <div class="ticket-notch left"></div>
                      <div class="ticket-notch right"></div>

                      <div class="ticket-head">
                        <h3 class="ticket-title">{{ truncateText(item.competitionId.title, 28) }}</h3>
                        <div class="status-tag" :class="getStatusClass(item.response_status)">{{ item.response_status }}</div>
                      </div>

                      <div class="ticket-perf"></div>

                      <div class="ticket-body">
                        <div class="ticket-row">
                          <span class="ticket-label">Agency</span>
                          <div class="organizer-info">
                            <img :src="item.agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <span>{{ item.agency_id?.name || "Unknown Agency" }}</span>
                          </div>
                        </div>
                        <div class="ticket-row">
                          <span class="ticket-label">Join Type</span>
                          <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                        </div>
                      </div>

                      <div class="ticket-footer">
                        <template v-if="item.response_status === 'Pending'">
                          <button class="action-btn success" @click="acceptInvitation(item)" :disabled="confirmingResponse == item._id">Accept</button>
                          <button class="action-btn danger" @click="rejectInvitation(item)" :disabled="rejectResponse == item._id">Reject</button>
                        </template>
                        <button v-else class="action-btn primary" @click="viewChallenge(item)">View</button>
                      </div>
                    </div>
                  </div>

                  <!-- Table View -->
                  <div v-else class="table-wrapper">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Challenge</th>
                          <th>Agency</th>
                          <th>Join Type</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in invitationsData" :key="index">
                          <td class="table-title">
                            <span class="status-dot" :class="getStatusClass(item.response_status)"></span>
                            {{ truncateText(item.competitionId.title, 35) }}
                          </td>
                          <td>
                            <div class="organizer-info">
                              <img :src="item.agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <span>{{ truncateName(item.agency_id?.name || "Unknown Agency", 20) }}</span>
                            </div>
                          </td>
                          <td>
                            <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                          </td>
                          <td>
                            <span class="status-tag" :class="getStatusClass(item.response_status)">{{ item.response_status }}</span>
                          </td>
                          <td>
                            <div class="table-actions">
                              <template v-if="item.response_status === 'Pending'">
                                <button class="action-btn success small" @click="acceptInvitation(item)" :disabled="confirmingResponse == item._id">Accept</button>
                                <button class="action-btn danger small" @click="rejectInvitation(item)" :disabled="rejectResponse == item._id">Reject</button>
                              </template>
                              <button v-else class="action-btn primary small" @click="viewChallenge(item)">View</button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ==================== REQUESTS TAB ==================== -->
              <div v-show="activeTab === 'requests'" class="tab-panel">
                <div v-if="loading" class="skeleton-grid">
                  <div v-for="i in 3" :key="i" class="skeleton-ticket">
                    <div class="skeleton-bar"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                </div>

                <div v-else-if="requestsCount === 0" class="empty-state">
                  <div class="empty-mark">00</div>
                  <h3 class="empty-title">No requests pending</h3>
                  <p class="empty-description">Requests to join or approve will show up here.</p>
                </div>

                <div v-else class="content-section">
                  <div class="control-row single">
                    <div class="control-group">
                      <div class="search-wrapper">
                        <v-icon size="16" color="#80DEEA">mdi-magnify</v-icon>
                        <input v-model="searchRequests" type="text" class="search-input" placeholder="Search requests…" />
                      </div>
                    </div>
                    <div class="control-group">
                      <div class="view-toggle">
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'grid' }"
                          @click="viewMode = 'grid'"
                          title="Grid View"
                        >
                          <v-icon size="20">mdi-view-grid</v-icon>
                        </button>
                        <button 
                          class="view-btn" 
                          :class="{ active: viewMode === 'table' }"
                          @click="viewMode = 'table'"
                          title="Table View"
                        >
                          <v-icon size="20">mdi-view-list</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Grid View -->
                  <div v-if="viewMode === 'grid'" class="ticket-grid" >
                    <div v-for="(item, index) in filteredRequests" :key="index" class="ticket-card" @click="viewRequested(item)">
                      <div class="ticket-bar" :class="getStatusClass(item.response_status)"></div>
                      <div class="ticket-notch left"></div>
                      <div class="ticket-notch right"></div>

                      <div class="ticket-head">
                        <h3 class="ticket-title">{{ truncateText(item.competitionId.title, 28) }}</h3>
                        <div class="status-tag" :class="getStatusClass(item.response_status)">{{ item.response_status }}</div>
                      </div>

                      <div class="ticket-perf"></div>

                      <div class="ticket-body">
                        <div class="ticket-row">
                          <span class="ticket-label">Requested By</span>
                          <div class="organizer-info">
                              <img v-if="profile.type === 'Agency'" :src="item.requestedBy?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <img v-else :src="item.volunteer_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <span v-if="profile.type === 'Agency'">{{ item.requestedBy?.name || "Unknown User" }}</span>
                            <span v-else>{{ item.volunteer_id?.first_name + " " + item.volunteer_id?.last_name || "Unknown User" }}</span>
                          </div>
                        </div>
                        <div class="ticket-row">
                          <span class="ticket-label">Agency</span>
                          <div class="organizer-info">
                            <img v-if="profile.type === 'Agency'" :src="item.agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <img v-else :src="item.joinedUnder_agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <img v-if="profile.type === 'Agency'" :src="item.agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                            <span v-else>{{ item.joinedUnder_agency_id?.name || "Unknown Agency" }}</span>
                          </div>
                        </div>
                        <div class="ticket-row split">
                          <div>
                            <span class="ticket-label">Join Type</span>
                            <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                          </div>
                          <div>
                            <span class="ticket-label">Date</span>
                            <span class="mono-date">{{ formatDate(item.response_date) }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="ticket-footer">
                        <template v-if="item.response_status === 'Pending'">
                          <template v-if="canTakeActionOnRequest(item)">
                            <button class="action-btn success" @click="acceptRequest(item)" :disabled="confirmingResponse == item._id">Accept</button>
                            <button class="action-btn danger" @click="rejectRequest(item)" :disabled="rejectResponse == item._id">Reject</button>
                          </template>
                          <template v-else>
                            <span class="awaiting-tag">Awaiting Response</span>
                            <!-- <button class="action-btn danger" @click="cancelRequest(item)">Cancel</button> -->
                          </template>
                        </template>
                        <template v-else>
                          <span class="status-tag" :class="getStatusClass(item.response_status)">{{ item.response_status }}</span>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Table View -->
                  <div v-else class="table-wrapper">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Challenge</th>
                          <th>Requested By</th>
                          <th>Agency</th>
                          <th>Join Type</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in filteredRequests" :key="index" @click="viewRequested(item)">
                          <td class="table-title">
                            <span class="status-dot" :class="getStatusClass(item.response_status)"></span>
                            {{ truncateText(item.competitionId.title, 35) }}
                          </td>
                          <td>
                            <div class="organizer-info">
                              <img v-if="profile.type === 'Agency'" :src="item.requestedBy?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <img v-else :src="item.volunteer_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <span v-if="profile.type === 'Agency'">{{ truncateName(item.requestedBy?.name || "Unknown User", 20) }}</span>
                              <span v-else>{{ truncateName(item.volunteer_id?.first_name + " " + item.volunteer_id?.last_name || "Unknown User", 20) }}</span>
                              
                            </div>
                          </td>
                          <td>
                            <div class="organizer-info">
                              <img v-if="profile.type === 'Agency'" :src="item.agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <img v-else :src="item.joinedUnder_agency_id?.midThumbnail || require('@/assets/default.jpg')" class="avatar-sm" />
                              <span v-if="profile.type === 'Agency'">{{ truncateName(item.agency_id?.name || "Unknown Agency", 20) }}</span>
                              <span v-else>{{ truncateName(item.joinedUnder_agency_id?.name || "Unknown Agency", 20) }}</span>
                            </div>
                          </td>
                          <td>
                            <span class="join-tag" :class="getJoinTypeClass(item.join_type)">{{ item.join_type || 'N/A' }}</span>
                          </td>
                          <td>
                            <span class="status-tag" :class="getStatusClass(item.response_status)">{{ item.response_status }}</span>
                          </td>
                          <td class="mono-date">{{ formatDate(item.response_date) }}</td>
                          <td>
                            <div class="table-actions">
                              <template v-if="item.response_status === 'Pending'">
                                <template v-if="canTakeActionOnRequest(item)">
                                  <button class="action-btn success small" @click="acceptRequest(item)" :disabled="confirmingResponse == item._id">Accept</button>
                                  <button class="action-btn danger small" @click="rejectRequest(item)" :disabled="rejectResponse == item._id">Reject</button>
                                </template>
                                <template v-else>
                                  <!-- <button class="action-btn danger small" @click="cancelRequest(item)">Cancel</button> -->
                                    <span class="awaiting-tag">Awaiting Response</span>
                                </template>
                              </template>
                              <template v-else>
                                <span class="status-tag small" :class="getStatusClass(item.response_status)">{{ item.response_status }}</span>
                              </template>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- ===== SNACKBAR ===== -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" bottom right timeout="4000" class="premium-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-text">{{ errorMessage }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" class="snackbar-action">✕</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  data() {
    return {
      activeTab: 'participated',
      activeFilter: 'all',
      search: '',
      searchMyCreated: '',
      searchRequests: '',
      sortBy: 'recent',
      viewMode: 'grid',
      participatedChallenges: [],
      myCreatedChallenges: [],
      invitationsData: [],
      requestsData: [],
      loading: false,
      confirmingResponse: "",
      rejectResponse: "",
      snackbar: false,
      errorMessage: null,
      snackbarColor: "error",
      snackbarIcon: "❌",
      sortOptions: [
        { text: 'Most Recent', value: 'recent' },
        { text: 'Oldest First', value: 'oldest' },
        { text: 'By Status', value: 'status' },
        { text: 'A-Z', value: 'title' },
      ],
      filters: [
        { label: 'All', value: 'all' },
        { label: 'Upcoming', value: 'upcoming' },
        { label: 'Ongoing', value: 'ongoing' },
        { label: 'Completed', value: 'completed' },
      ]
    };
  },

  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),

    participatedCount() {
      return this.participatedChallenges ? this.participatedChallenges.length : 0;
    },

    myCreatedCount() {
      return this.myCreatedChallenges ? this.myCreatedChallenges.length : 0;
    },

    invitationsCount() {
      return this.invitationsData ? this.invitationsData.length : 0;
    },

    requestsCount() {
      return this.requestsData ? this.requestsData.length : 0;
    },

filteredParticipated() {
  let items = [...this.participatedChallenges];

  // Filter by competition status
  if (this.activeFilter !== 'all') {
    items = items.filter(item => {
      const status = item.competitionId?.competition_status?.toLowerCase();
      return status === this.activeFilter;
    });
  }

  // Search filter
  if (this.search && this.search.trim()) {
    const searchLower = this.search.trim().toLowerCase();
    items = items.filter(item => {
      const title = (item.competitionId?.title || '').toLowerCase();
      const organizer = (item.createdBy?.name || '').toLowerCase();
      return title.includes(searchLower) || organizer.includes(searchLower);
    });
  }

  this.sortItems(items);
  return items;
},

    filteredMyCreated() {
      let items = [...this.myCreatedChallenges];

      if (this.searchMyCreated && this.searchMyCreated.trim()) {
        const searchLower = this.searchMyCreated.trim().toLowerCase();
        items = items.filter(item =>
          (item.title || '').toLowerCase().includes(searchLower)
        );
      }

      return items;
    },

    filteredRequests() {
      let items = [...this.requestsData];

      if (this.searchRequests && this.searchRequests.trim()) {
        const searchLower = this.searchRequests.trim().toLowerCase();
        items = items.filter(item => {
          const title = (item.competitionId?.title || '').toLowerCase();
          const requester = (item.requestedBy?.name || '').toLowerCase();
          return title.includes(searchLower) || requester.includes(searchLower);
        });
      }

      return items;
    }
  },

  methods: {
    ...mapActions({
      fetchEachCompetitions: "competition/fetchEachCompetitions",
      getCompetitionTeamInvitation: "competition/getCompetitionTeamInvitation",
      respondToCompetitionRequest: "competition/respondToCompetitionRequest",
      fetchParticipatedChallenges: "competition/fetchParticipatedChallenges",
      fetchRequests: "competition/fetchRequests"
    }),

    pad(n) {
      return n < 10 ? `0${n}` : `${n}`;
    },

    getFilterCount(filterValue) {
      if (filterValue === 'all') return this.participatedCount;
      return this.participatedChallenges.filter(item =>
        item.competitionId?.competition_status?.toLowerCase() === filterValue
      ).length;
    },

    sortItems(items) {
      switch(this.sortBy) {
        case 'recent':
          items.sort((a, b) => new Date(b.response_date || b.createdAt) - new Date(a.response_date || a.createdAt));
          break;
        case 'oldest':
          items.sort((a, b) => new Date(a.response_date || a.createdAt) - new Date(b.response_date || b.createdAt));
          break;
        case 'status':
          items.sort((a, b) => (a.response_status || '').localeCompare(b.response_status || ''));
          break;
        case 'title':
          items.sort((a, b) => {
            const titleA = (a.competitionId?.title || a.title || '').toLowerCase();
            const titleB = (b.competitionId?.title || b.title || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
          break;
      }
    },

    getJoinTypeClass(type) {
      const classes = {
        'Creator': 'creator',
        'Invitation': 'invitation',
        'Request': 'request',
      };
      return classes[type] || '';
    },

 getStatusClass(status) {
      const classes = {
        'Accepted': 'accepted',
        'Pending': 'pending',
        'Rejected': 'rejected',
        'Completed': 'completed',
        'Upcoming': 'upcoming',
        'Ongoing': 'ongoing'
      };
      return classes[status] || '';
    },

    canTakeActionOnRequest(item) {
      if (this.profile?.type === 'Agency') {
        return item.createdBy?._id === this.profile._id;
      }
      if (this.profile?.type === 'Volunteer') {
        return item.requestedBy?._id === this.profile._id;
      }
      return false;
    },

    async acceptRequest(item) {
      const params = {
        competition_id: item.competitionId?._id || item._id,
        response_status: "Accepted"
      };
      try {
        this.confirmingResponse = item._id;
        await this.respondToCompetitionRequest(params);
        this.showSuccessMessage("Request accepted successfully!");
        await this.loadAllData();
      } catch (error) {
        this.showErrorMessage(error?.response?.data?.message || "Failed to accept request.");
      } finally {
        this.confirmingResponse = "";
      }
    },

    async rejectRequest(item) {
      const params = {
        competition_id: item.competitionId?._id || item._id,
        response_status: "Rejected"
      };
      try {
        this.rejectResponse = item._id;
        await this.respondToCompetitionRequest(params);
        this.showSuccessMessage("Request declined successfully.");
        await this.loadAllData();
      } catch (error) {
        this.showErrorMessage(error?.response?.data?.message || "Failed to decline request.");
      } finally {
        this.rejectResponse = "";
      }
    },

    async cancelRequest() {
      try {
        this.showSuccessMessage("Request cancelled successfully.");
        await this.loadAllData();
      } catch (error) {
        this.showErrorMessage("Failed to cancel request.");
      }
    },

    async acceptInvitation(item) {
      const params = {
        competition_id: item.competitionId?._id || item._id,
        response_status: "Accepted"
      };
      try {
        this.confirmingResponse = item._id;
        await this.respondToCompetitionRequest(params);
        this.showSuccessMessage("Invitation accepted successfully!");
        await this.loadAllData();
      } catch (error) {
        this.showErrorMessage(error?.response?.data?.message || "Failed to accept invitation.");
      } finally {
        this.confirmingResponse = "";
      }
    },

    async rejectInvitation(item) {
      const params = {
        competition_id: item.competitionId?._id || item._id,
        response_status: "Rejected"
      };
      try {
        this.rejectResponse = item._id;
        await this.respondToCompetitionRequest(params);
        this.showSuccessMessage("Invitation declined successfully.");
        await this.loadAllData();
      } catch (error) {
        this.showErrorMessage(error?.response?.data?.message || "Failed to decline invitation.");
      } finally {
        this.rejectResponse = "";
      }
    },

    viewChallenge(item) {
      
        this.$router.push({
        name: "detailed-view",
        params: { id: item.competitionId?._id || item._id || item.competition_id }
      });
      
    },

    viewRequested(item){
      if(this.profile.type === "Volunteer"){
        this.$router.push({
          name: "my-challenge"
        });
      } else if(this.profile.type === "Agency"){
           this.$router.push({
        name: "detailed-view",
        params: { id: item.competitionId?._id || item._id || item.competition_id }
      });
      }
    },

    viewChallengeDetails(item) {
      this.$router.push({
        name: 'detailed-view',
        params: { id: item._id || item.competition_id }
      });
    },

    viewResponses(item) {
      this.$router.push({
        name: 'competition-responses',
        query: { _id: item._id || item.competition_id }
      });
    },

    respondToChallenge(item) {
      this.$router.push({
        name: "participated-responses",
        query: { _id: item.competitionId?._id || item._id || item.competition_id }
      });
    },

    explore() {
      if (this.profile.type === "Volunteer") {
        this.$router.push({ name: "my-challenge" });
      } else if (this.profile.type === "Agency") {
        this.$router.push({ name: "competition" });
      }
    },

    createChallenge() {
      this.$router.push({ name: "create-competition" });
    },

    truncateText(text, length = 30) {
      if (!text || typeof text !== "string") return "";
      return text.length > length ? text.slice(0, length) + "…" : text;
    },

    truncateName(name, length = 28) {
      if (!name) return "";
      return name.length > length ? name.slice(0, length) + "..." : name;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    async loadAllData() {
      try {
        this.loading = true;
        await this.loadParticipatedChallenges();
        await this.loadRequests();

        if (this.profile.type === 'Agency') {
          await this.loadMyCreatedChallenges();
          await this.loadInvitations();
        }

        this.$forceUpdate();
      } catch (e) {
        console.error('Error loading data:', e);
      } finally {
        this.loading = false;
      }
    },

    async loadParticipatedChallenges() {
      try {
        const response = await this.fetchParticipatedChallenges();
        this.participatedChallenges = JSON.parse(JSON.stringify(response || []));
      } catch (e) {
        console.error('Error loading participated:', e);
        this.participatedChallenges = [];
      }
    },

    async loadMyCreatedChallenges() {
      try {
        const response = await this.fetchEachCompetitions();
        this.myCreatedChallenges = JSON.parse(JSON.stringify(response || []));
      } catch (e) {
        console.error('Error loading my created:', e);
        this.myCreatedChallenges = [];
      }
    },

    async loadInvitations() {
      try {
        const response = await this.getCompetitionTeamInvitation();
        this.invitationsData = JSON.parse(JSON.stringify(response || []));
      } catch (e) {
        console.error('Error loading invitations:', e);
        this.invitationsData = [];
      }
    },

    async loadRequests() {
      try {
        const response = await this.fetchRequests();
        this.requestsData = JSON.parse(JSON.stringify(response || []));
      } catch (e) {
        console.error('Error loading requests:', e);
        this.requestsData = [];
      }
    },

    showSuccessMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    }
  },

  mounted() {
    this.loadAllData();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

/* ============ TOKENS ============ */
.scoreboard-container {
  --ink: #0A1628;
  --surface: #0D1F3C;
  --paper: #F0F4F8;
  --lime: #4FC3F7;
  --coral: #FF8A80;
  --blue: #64B5F6;
  --slate: #78909C;
  --line: rgba(255,255,255,0.08);

  position: relative;
  min-height: 100vh;
  padding: 24px;
  font-family: 'Inter', sans-serif;
  overflow-x: hidden;
}

.texture-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: repeating-linear-gradient(
    0deg, rgba(10,15,30,0.025) 0px, rgba(10,15,30,0.025) 1px, transparent 1px, transparent 3px
  );
}

.relative { position: relative; z-index: 1; }

/* ============ BREADCRUMB ============ */
.crumb-rail {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.crumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  transition: color 0.2s ease;
}

.crumb-item:hover { color: #fff; }
.crumb-item.active { color: #fff; font-weight: 600; cursor: default; }
.crumb-divider { color: rgba(255,255,255,0.3); }

/* ============ HERO SCOREBOARD ============ */
.hero-board {
  background: linear-gradient(135deg, rgba(8, 68, 104, 0.92), rgba(10, 91, 138, 0.92));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
}

.hero-board::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #4FC3F7, #81D4FA, #4FC3F7);
}

.hero-board-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 28px;
  padding: 44px 40px 40px;
}

.hero-left { max-width: 480px; }

.eyebrow {
  display: block;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: #4FC3F7;
  margin-bottom: 14px;
}

.hero-title {
  font-family: 'Archivo Black', sans-serif;
  font-size: 2.6rem;
  line-height: 0.98;
  color: #fff;
  margin: 0 0 14px 0;
  letter-spacing: -0.01em;
  text-transform: uppercase;
}

.hero-sub {
  color: rgba(255,255,255,0.7);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.hero-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.flip-tile {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 14px 22px;
  min-width: 96px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.flip-number {
  display: block;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 2rem;
  font-weight: 600;
  color: #fff;
  line-height: 1;
}

.flip-tile.accent-lime .flip-number { color: #4FC3F7; }
.flip-tile.accent-coral .flip-number { color: #FF8A80; }
.flip-tile.accent-blue .flip-number { color: #64B5F6; }

.flip-label {
  display: block;
  margin-top: 8px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.5);
  font-weight: 600;
}

/* ============ MAIN PANEL ============ */
.main-panel {
  border-radius: 16px !important;
  background: rgba(255,255,255,0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2) !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1) !important;
  margin-bottom: 30px;
  overflow: hidden;
}

/* ============ SEGMENT CONTROL ============ */
.segment-control {
  display: flex;
  background: rgba(8, 68, 104, 0.08);
  overflow-x: auto;
  padding: 4px;
  gap: 4px;
}

.segment-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.seg-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(10,22,40,0.6);
}

.segment-btn:hover .seg-label { color: rgba(10,22,40,0.9); }

.segment-btn.active {
  background: #fff;
  box-shadow: 0 4px 12px rgba(8, 68, 104, 0.15);
}

.segment-btn.active .seg-label { color: #0A1628; }

.seg-count {
  background: rgba(8, 68, 104, 0.1);
  color: #0A1628;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 20px;
}

.seg-count.lime { background: #4FC3F7; color: #fff; }
.seg-count.coral { background: #FF8A80; color: #fff; }
.seg-count.blue { background: #64B5F6; color: #fff; }

/* ============ PANEL BODY ============ */
.panel-body { padding: 28px; }

.tab-panel { animation: fadeSlide 0.25s ease; }

@keyframes fadeSlide {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ============ CONTROLS ============ */
.control-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.control-row.single { 
  justify-content: flex-start; 
}

.control-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F0F4F8;
  border: 1px solid rgba(10,22,40,0.08);
  border-radius: 10px;
  padding: 0 14px;
  flex: 1;
  min-width: 200px;
  transition: all 0.3s ease;
}

.search-wrapper:focus-within {
  border-color: #4FC3F7;
  box-shadow: 0 0 0 3px rgba(79, 195, 247, 0.1);
}

.search-input {
  border: none;
  background: transparent;
  padding: 11px 0;
  font-size: 13px;
  flex: 1;
  outline: none;
  color: #0A1628;
  font-family: 'Inter', sans-serif;
}

.search-input::placeholder { color: #78909C; }

.sort-select {
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(10,22,40,0.08);
  background: #F0F4F8;
  font-size: 12px;
  font-weight: 600;
  font-family: 'IBM Plex Mono', monospace;
  color: #0A1628;
  cursor: pointer;
  outline: none;
  height: 42px;
  transition: all 0.3s ease;
}

.sort-select:focus {
  border-color: #4FC3F7;
  box-shadow: 0 0 0 3px rgba(79, 195, 247, 0.1);
}

/* ============ VIEW TOGGLE ============ */
.view-toggle {
  display: flex;
  gap: 4px;
  background: #F0F4F8;
  border: 1px solid rgba(10,22,40,0.08);
  border-radius: 10px;
  padding: 3px;
}

.view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-btn:hover { background: rgb(7, 76, 181); }
.view-btn.active { 
  background: #0d3e88df;
  color: #ffffff;
}

/* ============ FILTER RAIL ============ */
.filter-rail {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 22px;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(10,22,40,0.1);
  background: #fff;
  color: #0A1628;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-tag:hover { border-color: #4FC3F7; }

.filter-tag.active {
  background: #0A1628;
  border-color: #0A1628;
  color: #4FC3F7;
}

.tag-count {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  opacity: 0.6;
}

/* ============ TICKET GRID ============ */
.ticket-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* ============ TICKET CARD ============ */
.ticket-card {
  position: relative;
  background: #fff;
  border: 1px solid rgba(10,22,40,0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.ticket-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(8, 68, 104, 0.15);
}

.ticket-bar {
  height: 5px;
  width: 100%;
  background: #78909C;
}

.ticket-bar.accepted, .ticket-bar.ongoing { background: linear-gradient(90deg, #4FC3F7, #81D4FA); }
.ticket-bar.pending { background: linear-gradient(90deg, #FFB74D, #FFA726); }
.ticket-bar.rejected { background: linear-gradient(90deg, #EF5350, #E53935); }
.ticket-bar.completed { background: linear-gradient(90deg, #78909C, #90A4AE); }
.ticket-bar.upcoming { background: linear-gradient(90deg, #64B5F6, #42A5F5); }

.ticket-notch {
  position: absolute;
  top: 34px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #F0F4F8;
  border: 1px solid rgba(10,22,40,0.08);
  z-index: 2;
}

.ticket-notch.left { left: -8px; }
.ticket-notch.right { right: -8px; }

.ticket-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 18px 20px 14px;
}

.ticket-title {
  font-family: 'Archivo Black', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #0A1628;
  margin: 0;
  line-height: 1.25;
  text-transform: uppercase;
  letter-spacing: -0.01em;
}

.status-tag {
  flex-shrink: 0;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(120,144,156,0.15);
  color: #78909C;
}

.status-tag.accepted, .status-tag.ongoing { background: rgba(79,195,247,0.15); color: #0288D1; }
.status-tag.pending { background: rgba(255,183,77,0.15); color: #E65100; }
.status-tag.rejected { background: rgba(239,83,80,0.15); color: #C62828; }
.status-tag.completed { background: rgba(120,144,156,0.15); color: #546E7A; }
.status-tag.upcoming { background: rgba(100,181,246,0.15); color: #1565C0; }

.status-tag.small { font-size: 9px; padding: 2px 8px; }

.ticket-perf {
  border-top: 1px dashed rgba(10,22,40,0.08);
  margin: 0 20px;
}

.ticket-body { padding: 16px 20px; }

.ticket-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
}

.ticket-row.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ticket-row.split > div { display: flex; flex-direction: column; gap: 3px; }

.ticket-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #78909C;
  font-weight: 600;
}

.organizer-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #0A1628;
}

.avatar-sm {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(10,22,40,0.08);
}

.join-tag {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: fit-content;
}

.join-tag.creator { background: rgba(100,181,246,0.15); color: #1565C0; }
.join-tag.invitation { background: rgba(255,183,77,0.15); color: #E65100; }
.join-tag.request { background: rgba(79,195,247,0.15); color: #0288D1; }

.mono-stat {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: #0A1628;
}

.mono-date {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  color: #0A1628;
}

.response-count {
  font-family: 'IBM Plex Mono', monospace;
  background: #0A1628;
  color: #4FC3F7;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
}

.awaiting-tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: rgba(100,181,246,0.1);
  color: #1565C0;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'IBM Plex Mono', monospace;
}

/* ============ TICKET FOOTER ============ */
.ticket-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(10,22,40,0.06);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  padding: 9px 14px;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #F0F4F8;
  color: #0A1628;
}

.action-btn.small {
  flex: none;
  padding: 6px 12px;
  font-size: 10px;
}

.action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.action-btn:active { transform: translateY(0); }

.action-btn.primary { background: #0A1628; color: #4FC3F7; }
.action-btn.success { background: linear-gradient(135deg, #4FC3F7, #81D4FA); color: #fff; }
.action-btn.warning { background: linear-gradient(135deg, #FFB74D, #FFA726); color: #fff; }
.action-btn.danger { background: transparent; color: #EF5350; border: 1px solid rgba(239,83,80,0.3); }

.action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
}

/* ============ TABLE VIEW ============ */
.table-wrapper {
  overflow-x: auto;
  margin: 16px 0;
  border-radius: 12px;
  border: 1px solid rgba(10,22,40,0.08);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 800px;
}

.data-table thead {
  background: linear-gradient(135deg, rgba(8, 68, 104, 0.92), rgba(10, 91, 138, 0.92));
}

.data-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.8);
  font-weight: 600;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid rgba(10,22,40,0.06);
  transition: background 0.2s ease;
}

.data-table tbody tr:hover {
  background: rgba(79,195,247,0.04);
}

.data-table tbody td {
  padding: 12px 16px;
  vertical-align: middle;
}

.data-table .table-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #0A1628;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.accepted, .status-dot.ongoing { background: #4FC3F7; }
.status-dot.pending { background: #FFB74D; }
.status-dot.rejected { background: #EF5350; }
.status-dot.completed { background: #78909C; }
.status-dot.upcoming { background: #64B5F6; }

.table-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* ============ EMPTY STATE ============ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 70px 20px;
  text-align: center;
}

.empty-mark {
  font-family: 'Archivo Black', sans-serif;
  font-size: 4rem;
  color: rgba(10,22,40,0.06);
  line-height: 1;
  margin-bottom: 8px;
}

.empty-title {
  font-family: 'Archivo Black', sans-serif;
  text-transform: uppercase;
  font-size: 1.2rem;
  color: #0A1628;
  margin: 0 0 8px 0;
}

.empty-description {
  font-size: 0.9rem;
  color: #78909C;
  max-width: 420px;
  margin: 0 0 22px 0;
  line-height: 1.5;
}

.btn-primary {
  padding: 12px 26px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(8, 68, 104, 0.92), rgba(10, 91, 138, 0.92));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(8, 68, 104, 0.3); }

/* ============ SKELETON ============ */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

.skeleton-ticket {
  background: #fff;
  border: 1px solid rgba(10,22,40,0.08);
  border-radius: 16px;
  padding: 18px 20px;
}

.skeleton-bar {
  height: 5px;
  margin: -18px -20px 18px;
  background: linear-gradient(90deg, #e0e7ef 25%, #eef2f7 50%, #e0e7ef 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-line {
  height: 11px;
  background: #e0e7ef;
  border-radius: 8px;
  margin-bottom: 10px;
  animation: shimmer 1.5s infinite;
}

.skeleton-line.short { width: 55%; }

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ============ SNACKBAR ============ */
.premium-snackbar { border-radius: 12px !important; }
.snackbar-text { font-weight: 500; font-size: 13px; font-family: 'Inter', sans-serif; }
.snackbar-action { color: #4FC3F7 !important; font-size: 16px !important; }

/* ============ RESPONSIVE ============ */
@media (max-width: 1200px) {
  .ticket-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 992px) {
  .hero-board-inner { 
    flex-direction: column; 
    align-items: flex-start; 
  }
  .hero-right { 
    width: 100%; 
  }
  .flip-tile { 
    flex: 1; 
    min-width: 80px; 
  }
  .control-row {
    flex-direction: column;
    align-items: stretch;
  }
  .control-group {
    width: 100%;
  }
  .search-wrapper {
    min-width: 0;
    width: 100%;
  }
  .sort-select {
    flex: 1;
  }
}

@media (max-width: 768px) {
  .scoreboard-container { padding: 14px; }
  .hero-board-inner { padding: 30px 22px; }
  .hero-title { font-size: 1.9rem; }
  .panel-body { padding: 18px; }
  .seg-label { display: none; }
  .segment-btn { padding: 12px 16px; }
  .ticket-grid, .skeleton-grid { 
    grid-template-columns: 1fr; 
  }
  .ticket-row.split { 
    grid-template-columns: 1fr; 
    gap: 6px; 
  }
  .ticket-footer { 
    flex-direction: column; 
  }
  .data-table tbody td,
  .data-table thead th {
    padding: 8px 10px;
    font-size: 12px;
  }
  .data-table {
    font-size: 12px;
    min-width: 600px;
  }
  .table-actions {
    flex-direction: column;
  }
  .action-btn.small {
    width: 100%;
  }
  .view-toggle {
    width: 100%;
    justify-content: center;
  }
  .control-row.single {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .hero-title { font-size: 1.5rem; }
  .flip-number { font-size: 1.5rem; }
  .flip-tile { 
    padding: 10px 14px; 
    min-width: 60px; 
  }
  .hero-board-inner { padding: 20px 16px; }
  .panel-body { padding: 12px; }
  .crumb-rail { font-size: 10px; }
  .ticket-head { 
    flex-direction: column;
    align-items: flex-start;
  }
  .ticket-title { font-size: 13px; }
  .filter-rail { gap: 4px; }
  .filter-tag { 
    padding: 4px 10px; 
    font-size: 10px; 
  }
  .data-table {
    font-size: 11px;
    min-width: 480px;
  }
  .data-table tbody td,
  .data-table thead th {
    padding: 6px 8px;
  }
  .organizer-info {
    font-size: 11px;
  }
  .avatar-sm {
    width: 18px;
    height: 18px;
  }
}
</style>